import VesselInformationService from "../../services/vessel_information/service";
import mapVesselPositionDataFromJson from "../../domain/vessel_information/vessel_position/mapper";
import { onConfig } from "../../config";

let vesselInformationService = null;

onConfig((config) => {
  vesselInformationService = new VesselInformationService(
    config.VUE_APP_BASIC_URL,
    mapVesselPositionDataFromJson
  );
});
const getDefaultState = () => {
  return {
    vesselPositionData: null,          // will become [] or array of points
    vesselPositionDataLoading: true,
    vesselPositionDataError: null,
  };
};

const state = getDefaultState();

const getters = {
  initialVesselCoordinates: (state) => {
    const d = state.vesselPositionData;
    if (!Array.isArray(d) || d.length === 0) return null;
    if (!d[0] || !d[0].coordinates) return null;
    return d[0].coordinates;
  },

  latestVesselCoordinates: (state) => {
    const d = state.vesselPositionData;
    if (!Array.isArray(d) || d.length === 0) return null;
    const last = d[d.length - 1];
    if (!last || !last.coordinates) return null;
    return last.coordinates;
  },

  voyageCoordinatesGeoJson: (state) => {
    const d = state.vesselPositionData;
    const points = Array.isArray(d) ? d : [];

    const voyageCoordinates = points
      .map(function (p) { return p && p.coordinates ? p.coordinates : null; })
      .filter(function (c) { return Array.isArray(c) && c.length === 2; });

    const pointFeatures = points
      .filter(function (p) { return p && Array.isArray(p.coordinates) && p.coordinates.length === 2; })
      .map(function (point) {
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: point.coordinates,
          },
          properties: {
            description: _generateFeatureDataHTML(point),
          },
        };
      });

    // Only include LineString when it can actually render (>= 2 points)
    let features = [];
    if (voyageCoordinates.length >= 2) {
      features.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: voyageCoordinates,
        },
        properties: {
          id: "voyageCoordinates",
        },
      });
    }

    // Always include points (even if only 1)
    features = features.concat(pointFeatures);

    return {
      type: "geojson",
      data: {
        id: "voyageCoordinates",
        type: "FeatureCollection",
        features: features,
      },
    };
  },

  // eslint-disable-next-line no-unused-vars
  voyageCoordinateLineLayer: (_) => {
    return {
      id: "voyageCoordinatesLine",
      type: "line",
      paint: {
        "line-color": "#BDBDBD",
        "line-width": 6,
      },
      filter: ["==", "$type", "LineString"],
    };
  },

  // eslint-disable-next-line no-unused-vars
  voyageCoordinatePointLayer: (_) => {
    return {
      id: "voyageCoordinatesPoint",
      type: "circle",
      paint: {
        "circle-radius": 5.5,
        "circle-color": "#BDBDBD",
        "circle-stroke-width": 1,
        "circle-stroke-color": "#FFFFFF",
      },
      filter: ["==", "$type", "Point"],
    };
  },
};

const actions = {
  async fetchVoyageVesselPositionData({ commit }, payload) {
    commit("FETCH_VOYAGE_VESSEL_POSITION_DATA_LOADING");

    const vesselName = payload.name;
    const startDate = payload.startDate;
    const stopDate = payload.stopDate;

    try {
      let response = await vesselInformationService.getVoyageVesselPosition(
        vesselName,
        startDate,
        stopDate
      );

      // Normalize: always store array
      if (!Array.isArray(response)) response = [];

      commit("FETCH_VOYAGE_VESSEL_POSITION_DATA_SUCCESS", response);
    } catch (error) {
      commit("FETCH_VOYAGE_VESSEL_POSITION_DATA_ERROR", error);
    }
  },
};

const mutations = {
  FETCH_VOYAGE_VESSEL_POSITION_DATA_LOADING: (state) => {
    state.vesselPositionDataLoading = true;
    state.vesselPositionDataError = null;

    // Optional: if you WANT the map to "clear" while loading:
    // state.vesselPositionData = null;
  },

  FETCH_VOYAGE_VESSEL_POSITION_DATA_ERROR: (state, error) => {
    state.vesselPositionDataLoading = false;
    state.vesselPositionDataError = error;
  },

  FETCH_VOYAGE_VESSEL_POSITION_DATA_SUCCESS: (state, data) => {
    state.vesselPositionDataLoading = false;
    state.vesselPositionDataError = null;
    state.vesselPositionData = Array.isArray(data) ? data : [];
  },
};

function formatEta(eta) {
  if (!eta) return "No data available";

  const date = new Date(eta);

  if (isNaN(date.getTime()) || date.getTime() === 0) {
    return "No data available";
  }

  return date.toLocaleString();
}

function _generateFeatureDataHTML(point) {
  const totalConsumption = point.totalConsumption !== null ? point.totalConsumption : 0;

  const formattedDate = new Date(point.insertDate).toLocaleString();
  const formattedEtaNextPort = formatEta(point.etaNextPort);

  return (
    "\n        <div class=\"vessel-popup-content\">\n" +
        "            <div class=\"popup-title\">Position Data</div>\n" +
        "            <div class=\"popup-item\">\n" +
        "                <i class=\"mdi mdi-clock-outline\"></i>\n" +
        "                <div class=\"popup-item-content\">\n" +
        "                    <span class=\"popup-label\">Time</span>\n" +
        "                    <span class=\"popup-value\">" + formattedDate + "</span>\n" +
        "                </div>\n" +
        "            </div>\n" +
        "            <div class=\"popup-item\">\n" +
        "                <i class=\"mdi mdi-map-marker-outline\"></i>\n" +
        "                <div class=\"popup-item-content\">\n" +
        "                    <span class=\"popup-label\">ETA Next Port</span>\n" +
        "                    <span class=\"popup-value\">" + formattedEtaNextPort + "</span>\n" +
        "                </div>\n" +
        "            </div>\n" +
        "            <div class=\"popup-item\">\n" +
        "                <i class=\"mdi mdi-gas-station-outline\"></i>\n" +
        "                <div class=\"popup-item-content\">\n" +
        "                    <span class=\"popup-label\">Daily Fuel Consumption</span>\n" +
        "                    <span class=\"popup-value\">" + totalConsumption.toFixed(3) + " metric tons</span>\n" +
        "                </div>\n" +
        "            </div>\n" +
        "        </div>\n    "
  );
}

export const vesselPositionModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
